import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import API from '../utils/api';
import Seo from '../components/Seo';
import AptitudePractice from './AptitudePractice';

const V1 = '#7F00FF', V2 = '#B163FF';

const LockIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="0" y1="420" x2="210" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" /><circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const STATUS_CONFIG = {
  pending_payment: {
    emoji: '⏳', color: 'var(--warning)', bg: 'rgba(245,166,35,0.1)', border: 'rgba(245,166,35,0.3)',
    title: 'Payment Verification Pending',
    body: 'Hang tight! Our team is reviewing your payment and will unlock Aptitude Practice shortly.',
    sub: 'Verification usually takes 1–2 hours.',
  },
};

const AptitudeWorkspace = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    API.get(`/aptitude/my/${id}`, { params: { email } })
      .then(r => setEnrollment(r.data))
      .catch(err => setError(err.response?.data?.message || 'Could not find this enrollment.'))
      .finally(() => setLoading(false));
  }, [id, email]);

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      <Seo
        title="Aptitude Practice — Part 1"
        description="TCS, Infosys, Wipro and Zoho level aptitude questions with real step-by-step explanations, official shortcut methods, and community-shared optimized answers."
        path="/aptitude-workspace"
      />

      {(!loading && !error && enrollment && enrollment.status === 'active') ? (
        <AptitudePractice />
      ) : (
        <section style={{ padding: '140px 0 100px', position: 'relative', overflow: 'hidden' }}>
          <CircuitBg />
          <div style={{ position: 'absolute', bottom: 0, right: 0, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 2, margin: '0 auto', maxWidth: 620, textAlign: 'center', padding: '0 20px' }}>

            {loading && (
              <p style={{ color: 'var(--muted)', fontSize: 15 }}>Loading your enrollment…</p>
            )}

            {!loading && error && (
              <div style={{
                background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20,
                padding: '48px 32px', boxShadow: 'var(--shadow-card)',
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', margin: '0 auto 20px',
                  background: 'rgba(240,65,85,0.1)', color: 'var(--danger)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><LockIcon /></div>
                <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10, fontFamily: "'Inter',sans-serif" }}>Enrollment Not Found</h1>
                <p style={{ color: 'var(--muted)', fontSize: 14.5, marginBottom: 24 }}>{error}</p>
                <Link to="/dsa" className="btn btn-primary" style={{ display: 'inline-flex' }}>Back to Programs</Link>
              </div>
            )}

            {!loading && !error && enrollment && enrollment.status !== 'active' && (() => {
              const sc = STATUS_CONFIG[enrollment.status] || STATUS_CONFIG.pending_payment;
              return (
                <div style={{
                  background: 'var(--card-solid)', border: `1px solid ${sc.border}`, borderRadius: 20,
                  padding: '52px 36px', boxShadow: 'var(--shadow-card)',
                }}>
                  <div style={{ fontSize: 44, marginBottom: 18 }}>{sc.emoji}</div>
                  <h1 style={{
                    fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, marginBottom: 12,
                    fontFamily: "'Inter',sans-serif", color: sc.color,
                  }}>{sc.title}</h1>
                  <p style={{ color: 'var(--text-2)', fontSize: 15.5, lineHeight: 1.7, marginBottom: 8, maxWidth: 460, margin: '0 auto 8px' }}>{sc.body}</p>
                  <p style={{ color: 'var(--muted)', fontSize: 13.5 }}>{sc.sub}</p>

                  <div style={{
                    marginTop: 32, display: 'inline-flex', flexDirection: 'column', gap: 4,
                    background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12,
                    padding: '14px 22px', fontSize: 13,
                  }}>
                    <span style={{ color: 'var(--muted)', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: 11 }}>Enrollment ID</span>
                    <span style={{ color: 'var(--text)', fontWeight: 700, fontFamily: 'monospace' }}>{enrollment.enrollmentId}</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>
      )}
    </div>
  );
};

export default AptitudeWorkspace;
